import { SerializedPageContext, usePageContext } from 'hooks/context';

export interface RouteContext {
  /** Locale as used in the url (can be null in case there is only 1 locale) */
  prefix: string | null;
  /** Active locale with language and country code */
  locale: string;
  /** All available locales for this site */
  locales: { [prefix: string]: string };
  /** Current path without the locale prefix */
  path: string;
  /** Domain */
  host: string;
  /** Path name as in window.location.pathname, clean path without query */
  pathname: string;

  /** Resolve absolute urls and append the current language prefix to the given url if there is none */
  to(url: string, queryParams?: Record<string, string>): string;

  to(
    url: string | null | undefined,
    queryParams?: Record<string, string>,
  ): string | undefined;
}

/**
 * @deprecated Use the next.js router on app router pages instead
 */
export function useRoute(): RouteContext {
  const { route } = usePageContext();
  if (route.host == null) {
    throw new Error(
      'route context is not available on the app router, use the next.js router instead',
    );
  }
  return route;
}

export function createRouter(
  config: SerializedPageContext['config'],
  route: SerializedPageContext['route'],
): RouteContext {
  return {
    prefix: route.prefix,
    locale: route.locale,
    path: route.path,
    host: route.host,
    locales: route.locales,
    pathname: route.path?.split('?')[0],
    to(
      url: string | null | undefined,
      queryParams?: Record<string, string>,
    ): string | undefined {
      if (!url) return undefined;
      let result = url;
      if (!url.match(/^https?:/)) {
        result = url.startsWith('/') ? url : `/${url}`;
        const existing = result.split('/')[1];
        if (
          config.locales &&
          !Object.keys(config.locales).includes(existing) &&
          route.prefix
        ) {
          result = `/${route.prefix}${result}`;
        }
      }
      if (queryParams) {
        const params = Object.entries(queryParams as Record<string, string>)
          .filter(([, value]) => value)
          .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
          .join('&');
        result += (url.includes('?') ? '&' : '?') + params;
      }
      return result;
    },
  } as RouteContext;
}
